import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert, useTheme, Grid,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Share,
  Favorite,
  Schedule,
  Visibility,
  Launch,
  Facebook,
  Instagram,
  YouTube
} from '@mui/icons-material';
import annonceService from '../services/annonceService';

const AnnonceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [annonce, setAnnonce] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const theme = useTheme();

  // Scroll vers le haut quand on arrive sur la page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    const fetchAnnonceAndData = async () => {
      try {
        setLoading(true);
        // Récupérer l'annonce actuelle
        const annonceData = await annonceService.getAd(id);
        setAnnonce(annonceData.ad);

        // Récupérer la liste des annonces pour la navigation
        const annoncesData = await annonceService.getAds({ limit: 100 });
        setAnnonces(annoncesData.ads || []);

        setError('');
        setCurrentMediaIndex(0); // Réinitialiser l'index quand on charge une nouvelle annonce
      } catch (err) {
        console.error('Erreur lors du chargement de l\'annonce:', err);
        setError('Annonce non trouvée');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAnnonceAndData();
    }
  }, [id]);

  // Navigation entre annonces
  const getCurrentAnnonceIndex = () => {
    return annonces.findIndex(a => a.id === id);
  };

  const getPreviousAnnonce = () => {
    const currentIndex = getCurrentAnnonceIndex();
    if (currentIndex > 0) {
      return annonces[currentIndex - 1];
    }
    return null;
  };

  const getNextAnnonce = () => {
    const currentIndex = getCurrentAnnonceIndex();
    if (currentIndex < annonces.length - 1) {
      return annonces[currentIndex + 1];
    }
    return null;
  };

  const handlePreviousAnnonce = () => {
    const previousAnnonce = getPreviousAnnonce();
    if (previousAnnonce) {
      navigate(`/annonces/${previousAnnonce.id}`);
    }
  };

  const handleNextAnnonce = () => {
    const nextAnnonce = getNextAnnonce();
    if (nextAnnonce) {
      navigate(`/annonces/${nextAnnonce.id}`);
    }
  };

  // Raccourcis clavier pour la navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft' && getPreviousAnnonce()) {
        handlePreviousAnnonce();
      } else if (event.key === 'ArrowRight' && getNextAnnonce()) {
        handleNextAnnonce();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [annonces, id]);

  const handleBack = () => {
    navigate('/annonces');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: annonce?.title,
        text: annonce?.description,
        url: window.location.href,
      });
    } else {
      // Fallback: copier l'URL dans le presse-papiers
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié dans le presse-papiers !');
    }
  };

  const handleVisitTarget = () => {
    if (annonce?.targetUrl) {
      let url = annonce.targetUrl;

      // Si l'URL ne commence pas par http:// ou https://, on ajoute https://
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSocialMediaClick = (url) => {
    if (url) {
      let socialUrl = url;

      // Si l'URL ne commence pas par http:// ou https://, on ajoute https://
      if (!socialUrl.startsWith('http://') && !socialUrl.startsWith('https://')) {
        socialUrl = 'https://' + socialUrl;
      }

      window.open(socialUrl, '_blank', 'noopener,noreferrer');
    }
  };


  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Retour aux annonces
        </Button>
      </Container>
    );
  }

  if (!annonce) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Annonce non trouvée
        </Typography>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Retour aux annonces
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          sx={{ textDecoration: 'none' }}
        >
          Accueil
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/annonces')}
          sx={{ textDecoration: 'none' }}
        >
          Annonces
        </Link>
        <Typography variant="body2" color="text.primary">
          {annonce.title}
        </Typography>
      </Breadcrumbs>

      {/* Navigation Annonces */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/annonces')}
          variant="outlined"
        >
          Retour
        </Button>
        {annonces.length > 1 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {getCurrentAnnonceIndex() + 1} / {annonces.length}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<ArrowBack />}
                variant="outlined"
                size="small"
                onClick={handlePreviousAnnonce}
                disabled={!getPreviousAnnonce()}
                title={getPreviousAnnonce() ? `Préc: ${getPreviousAnnonce().title}` : 'Première annonce'}
              >
                Préc
              </Button>
              <Button
                endIcon={<ArrowForward />}
                variant="outlined"
                size="small"
                onClick={handleNextAnnonce}
                disabled={!getNextAnnonce()}
                title={getNextAnnonce() ? `Suiv: ${getNextAnnonce().title}` : 'Dernière annonce'}
              >
                Suiv
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>

      {/* Contenu principal */}
      <Card sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            height: { xs: "auto", md: 550 },
            overflow: "hidden",
          }}
        >
          {/* ---- COLONNE GAUCHE: Images ---- */}
          <Box
            sx={{
              width: { xs: "100%", md: "45%" },
              height: { xs: 300, md: "100%" },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {(() => {
              // Gérer la migration depuis imageUrl vers images
              const images = annonce.images && Array.isArray(annonce.images) && annonce.images.length > 0
                ? annonce.images
                : (annonce.imageUrl ? [annonce.imageUrl] : []);
              
              // Gérer l'affichage (image ou vidéo)
              const hasVideo = !!annonce.video;
              const totalMedia = images.length + (hasVideo ? 1 : 0);
              
              // Déterminer ce qui est actuellement affiché
              const isShowingVideo = hasVideo && currentMediaIndex === images.length;
              const currentImageIndex = isShowingVideo ? -1 : currentMediaIndex;
              
              // Si aucune image et pas de vidéo
              if (images.length === 0 && !hasVideo) {
                return (
                  <>
                    <CardMedia
                      component="img"
                      image="/img/no_media.jpg"
                      alt={annonce.title}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                    <IconButton
                      onClick={handleShare}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "white" },
                        zIndex: 1,
                      }}
                      title="Partager"
                    >
                      <Share />
                    </IconButton>
                  </>
                );
              }
              
              // Si seulement une vidéo (pas d'images)
              if (images.length === 0 && hasVideo) {
                return (
                  <>
                    <CardMedia
                      component="video"
                      src={annonce.video}
                      controls
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        bgcolor: "#000",
                      }}
                    />
                    <IconButton
                      onClick={handleShare}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "white" },
                        zIndex: 1,
                      }}
                      title="Partager"
                    >
                      <Share />
                    </IconButton>
                  </>
                );
              }
              
              // Afficher l'image ou la vidéo actuelle
              return (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>
                  <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                    {isShowingVideo ? (
                      <CardMedia
                        component="video"
                        src={annonce.video}
                        controls
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          bgcolor: "#000",
                        }}
                      />
                    ) : (
                      <CardMedia
                        component="img"
                        image={images[currentImageIndex]}
                        alt={annonce.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    )}
                    <IconButton
                      onClick={handleShare}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(255,255,255,0.9)",
                        "&:hover": { bgcolor: "white" },
                        zIndex: 1,
                      }}
                      title="Partager"
                    >
                      <Share />
                    </IconButton>
                  </Box>
                  
                  {/* Miniatures : images + vidéo */}
                  {totalMedia > 1 && (
                    <Box sx={{ 
                      display: "flex", 
                      gap: 1, 
                      p: 1, 
                      overflowX: "auto",
                      borderTop: "1px solid #eee"
                    }}>
                      {images.map((img, index) => (
                        <CardMedia
                          key={index}
                          component="img"
                          image={img}
                          alt={`${annonce.title} ${index + 1}`}
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 1,
                            cursor: "pointer",
                            border: currentImageIndex === index ? "2px solid primary.main" : "2px solid transparent",
                            "&:hover": {
                              opacity: 0.8
                            }
                          }}
                          onClick={() => {
                            setCurrentMediaIndex(index);
                          }}
                        />
                      ))}
                      {hasVideo && (
                        <Box
                          onClick={() => {
                            setCurrentMediaIndex(images.length);
                          }}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            cursor: "pointer",
                            border: isShowingVideo ? "2px solid primary.main" : "2px solid transparent",
                            position: "relative",
                            overflow: "hidden",
                            bgcolor: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "&:hover": {
                              opacity: 0.8
                            }
                          }}
                        >
                          <CardMedia
                            component="video"
                            src={annonce.video}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              pointerEvents: "none",
                            }}
                            muted
                            onMouseEnter={(e) => {
                              e.target.play();
                            }}
                            onMouseLeave={(e) => {
                              e.target.pause();
                              e.target.currentTime = 0;
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "white",
                              bgcolor: "rgba(0,0,0,0.5)",
                              borderRadius: "50%",
                              width: 32,
                              height: 32,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pointerEvents: "none",
                            }}
                          >
                            <Box
                              component="svg"
                              sx={{
                                width: 20,
                                height: 20,
                                fill: "white",
                              }}
                              viewBox="0 0 24 24"
                            >
                              <path d="M8 5v14l11-7z" />
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>

          {/* ---- COLONNE DROITE: Contenu ---- */}
          <Box
            sx={{
              width: { xs: "100%", md: "55%" },
              height: { xs: "auto", md: "100%" },
              overflowY: "auto",
              p: 3,
              // Cacher les scrollbars uniquement sur mobile
              '&::-webkit-scrollbar': {
                display: { xs: 'none', md: 'block' }
              },
              // Firefox - cacher sur mobile
              '@media (max-width: 899px)': {
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              },
              // Firefox - afficher sur desktop
              '@media (min-width: 900px)': {
                scrollbarWidth: 'auto',
                msOverflowStyle: 'auto',
              },
            }}
          >
            {/* Titre */}
            <Typography
              variant="h4"
              sx={{
                wordBreak: "break-word",
                mb: 2,
              }}
            >
              {annonce.title}
            </Typography>

            {/* Description scrollable (à l’intérieur de la colonne droite) */}
            {annonce.description && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="h6" gutterBottom>Description</Typography>

                <Box
                  sx={{
                    maxHeight: 170,
                    overflowY: "auto",
                    pr: 1,
                    border: "1px solid #eee",
                    borderRadius: 1,
                    p: 1,
                    // Cacher les scrollbars uniquement sur mobile
                    '&::-webkit-scrollbar': {
                      display: { xs: 'none', md: 'block' }
                    },
                    // Firefox - cacher sur mobile
                    '@media (max-width: 899px)': {
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                    },
                    // Firefox - afficher sur desktop
                    '@media (min-width: 900px)': {
                      scrollbarWidth: 'auto',
                      msOverflowStyle: 'auto',
                    },
                  }}
                >
                  <Typography variant="body1">{annonce.description}</Typography>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Infos */}
            <Typography variant="h6" gutterBottom>
              Informations
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <span>Publiée le : {new Date(annonce.createdAt).toLocaleDateString("fr-FR")}</span>
              {annonce.startDate && (
                <>
                  <span>|</span>
                  <span>Début : {new Date(annonce.startDate).toLocaleDateString("fr-FR")}</span>
                </>
              )}
              {annonce.endDate && (
                <>
                  <span>|</span>
                  <span>Fin : {new Date(annonce.endDate).toLocaleDateString("fr-FR")}</span>
                </>
              )}
            </Typography>

            {/* Bouton d'action */} 
            {annonce.targetUrl && (
              <Box sx={{ mt: 1, textAlign: 'center' }}> 
              <Button variant="contained" size="large" 
              startIcon={<Launch />} 
              onClick={handleVisitTarget}
              sx={{ px: 4, py: 1, fontSize: '1.1rem', borderRadius: 2, background: theme.palette.color.degradeOne, }} >
               Suivre le lien joint à l'annonce 
               </Button> 
               </Box>
              )}

            {/* Réseaux sociaux */}
            {(annonce.facebookUrl || annonce.instagramUrl || annonce.snapchatUrl || annonce.tiktokUrl || annonce.youtubeUrl) && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Suivez-nous sur les réseaux sociaux
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                  {annonce.facebookUrl && (
                    <IconButton
                      size="medium"
                      onClick={() => handleSocialMediaClick(annonce.facebookUrl)}
                      sx={{
                        color: '#1877F2',
                        '&:hover': { bgcolor: 'rgba(24, 119, 242, 0.1)' }
                      }}
                      title="Facebook"
                    >
                      <Facebook />
                    </IconButton>
                  )}
                  {annonce.instagramUrl && (
                    <IconButton
                      size="medium"
                      onClick={() => handleSocialMediaClick(annonce.instagramUrl)}
                      sx={{
                        color: '#E4405F',
                        '&:hover': { bgcolor: 'rgba(228, 64, 95, 0.1)' }
                      }}
                      title="Instagram"
                    >
                      <Instagram />
                    </IconButton>
                  )}
                  {annonce.snapchatUrl && (
                    <IconButton
                      size="medium"
                      onClick={() => handleSocialMediaClick(annonce.snapchatUrl)}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                      }}
                      title="Snapchat"
                    >
                      <Box
                        component="img"
                        src="/img/snapchat-brands-solid-full.svg"
                        alt="Snapchat"
                        sx={{
                          width: 24,
                          height: 24,
                          objectFit: 'contain'
                        }}
                      />
                    </IconButton>
                  )}
                  {annonce.tiktokUrl && (
                    <IconButton
                      size="medium"
                      onClick={() => handleSocialMediaClick(annonce.tiktokUrl)}
                      sx={{
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                      }}
                      title="TikTok"
                    >
                      <Box
                        component="img"
                        src="/img/tiktok-brands-solid-full.svg"
                        alt="TikTok"
                        sx={{
                          width: 24,
                          height: 24,
                          objectFit: 'contain'
                        }}
                      />
                    </IconButton>
                  )}
                  {annonce.youtubeUrl && (
                    <IconButton
                      size="medium"
                      onClick={() => handleSocialMediaClick(annonce.youtubeUrl)}
                      sx={{
                        color: '#FF0000',
                        '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' }
                      }}
                      title="YouTube"
                    >
                      <YouTube />
                    </IconButton>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Card>


    </Container>
  );
};

export default AnnonceDetails;
